import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import CustomButton from "../../components/CustomButton";
import BookingSuccessModal from './components/BookingSuccessModal';
import ConfirmBookingModal from './components/ConfirmBookingModal';
import Banner from './components/Banner';
import Api from "../../services/Api";
import DatePicker from "../../components/Calendar/DatePicker";
import {dateFormatDate, dateFormatHourMinute} from "../../helpers/dateFunctions";
import {getLanguage} from "../../lang/helpers";

interface TimeSlot {
    start: string
    end: string
    startDate: Date
    endDate: Date
}

interface CoachInterface {
    id: number
    firstName: string
    lastName: string
    meetingLength: number
}

interface Props {
  fromMeeting?: boolean
  close?: Function
}

const BookMeetingPage: React.FC<Props> = ({fromMeeting, close}) => {
    const api = Api.getInstance();
    const translation = useTranslation();
    const { t, i18n } = translation;
    const lang = getLanguage(i18n.language);
    const queryParams = new URLSearchParams(window.location.search);
    const meetingId = queryParams.get('meeting_id');
    const navigate = useNavigate();

    const [loaded, setLoaded] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null);
    const [bookedMeeting, setBookedMeeting] = useState<TimeSlot | null>(null)
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [bookableDays, setBookableDays] = useState<Array<Date>>([])
    const [meetingDate, setMeetingDate] = useState<Date|null>(null)
    const [timeslots, setTimeslots] = useState<TimeSlot[]>([]); // To be fetched from backend
    const [coach, setCoach] = useState<CoachInterface|null>();
    const [isConfirmBookingModalOpen, setIsConfirmBookingModalOpen] = useState<boolean>(false);
    const [isBookingSuccessModalOpen, setIsBookingSuccessModalOpen] = useState<boolean>(false);

    useEffect(() => {
        fetchAvailableTimes();
    }, []);

    const fetchAvailableTimes = () => {
        let url = 'client/meeting_availability'
        if (meetingId) {
            url += '?meeting_id=' + meetingId
        }
        api.get(url).then(response => {
          if (response.json.error) {
            setError(response.json.error)
          } else {
            const timeslots: Array<TimeSlot> = []
            const _bookableDays: Array<Date> = []
            for (const timeslot of response.json.timeslots) {
              timeslot.startDate = new Date(timeslot.start)
              timeslot.endDate = new Date(timeslot.end)
              timeslots.push(timeslot)
              _bookableDays.push(timeslot.startDate)
            }
            setCoach(response.json.coach)
            setTimeslots(timeslots)
            if (response.json.bookedMeeting) {
              let bookedMeeting: TimeSlot = response.json.bookedMeeting
              bookedMeeting.startDate = new Date(bookedMeeting.start)
              bookedMeeting.endDate = new Date(bookedMeeting.end)
              setBookedMeeting(bookedMeeting)
            }
            setBookableDays(_bookableDays)
          }

          setLoaded(true)
        })
    };

    const closeBooking = () => {
      if (close) {

      } else {
        navigate('/videocoach')
      }
    }

    const closeWindow = () => {
      setIsBookingSuccessModalOpen(false)
      if (fromMeeting && close) {
        close()
      }
    }

    const bookMeeting = () => {
        if (!meetingDate || !coach) return;

        const data = {
            from: meetingDate,
            length: coach.meetingLength,
            id: meetingId ? meetingId : "0"
        }

        api.post('client/meeting_book', data).then(() => {
          setIsBookingSuccessModalOpen(true);
          fetchAvailableTimes()
        })
    };

    const getBookableMeetings = (day: Date) => {
        const startEvents: Array<Date> = []
        for (const timeslot of timeslots) {
            if (timeslot.startDate.getFullYear() != day.getFullYear() ||
              timeslot.startDate.getMonth() != day.getMonth() ||
              timeslot.startDate.getDate() != day.getDate()) {
                continue
            }
            startEvents.push(timeslot.startDate)
        }
        return startEvents
    }

    if (!loaded) {
      return <div>Loading...</div>;
    }

    if (error) {
      let errorMsg = error
      if (error === 'bookable_after_first_meeting') {
        errorMsg = lang === 'sv' ? 'Du kan boka fler möten efter ditt första möte.' :
                                 'You will be able to book more meetings after your first session.'
      } else if (error === 'no_remaining_meetings') {
        // Client has booked all meetings, send them to video coach start page
        if (fromMeeting) {
          errorMsg = lang === 'sv' ? 'Du har bokat alla dina möten.' :
            'You have booked all your meetings.'
        } else {
          navigate('/videocoach')
        }
      }

      return (
        <div className="main-content">
          <div className="z-title1 text-white">
            {lang === 'sv' ? 'Du kan inte boka möte' : 'You cannot book a meeting'}
          </div>
          <div className="mt-2">{errorMsg}</div>
        </div>
      )
    }

  return (
    <div className="main-content">
      {
        bookedMeeting &&
          <Banner
              selectedDate={bookedMeeting.startDate}
                  coachName={coach ? coach.firstName : "Unknown"}
                  close={() => closeBooking()}
                  title={t('change-meeting')} />
            }
            <div className="z-title1 text-white">
                {t('select-datetime')}
            </div>
            <DatePicker dates={bookableDays} clickDate={(d: Date) => setSelectedDate(d)}
                        preSelectedDate={selectedDate}
            />

            {
              selectedDate ? (
              <div className="mt-8">
                  <div className="flex justify-between items-end">
                      <div className="z-title1">
                        {lang === 'sv' ? 'Lediga tider' : 'Available times'}
                      </div>
                      <div className="flex">
                          <div className="ml-2">{dateFormatDate(selectedDate, false, lang)}</div>
                      </div>
                  </div>

                  <div className="grid grid-cols-3 gap-x-2">
                      {
                          getBookableMeetings(selectedDate).map((d: Date, i) => {
                              return (
                                <div key={"time_" + i} className="mt-2">
                                    <CustomButton buttonType="outlineWhiteBase"
                                            onClick={() => {
                                                setMeetingDate(d)
                                                setIsConfirmBookingModalOpen(true)
                                            }}
                                            className="w-full">
                                        {dateFormatHourMinute(d)}
                                    </CustomButton>
                                </div>
                              )
                          })
                      }
                  </div>

              </div>
              ) : (
                <div className="mt-8">
                  {lang === 'sv' ? 'Välj inringat datum (där det finns mötestider)' :
                    'Choose circled date (where there are available meeting times)'}
                </div>
              )
            }

            <ConfirmBookingModal
                isOpen={isConfirmBookingModalOpen}
                onClose={() => setIsConfirmBookingModalOpen(false)}
                selectedDate={meetingDate}
                coachName={coach ? coach.firstName : "Unknown"}
                meetingId={meetingId}
                meetingLength={coach?.meetingLength}
                onClickButton={() => {
                    bookMeeting();
                    setIsConfirmBookingModalOpen(false);
                }}
            />
            <BookingSuccessModal
                isOpen={isBookingSuccessModalOpen}
                fromMeeting={fromMeeting}
                onClose={() => closeWindow()}
                selectedDate={meetingDate}
                coachName={coach ? coach.firstName : "Unknown"}
                meetingId={meetingId}
            />
        </div>
    );
};

export default BookMeetingPage;
